import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheckCircle, FiSmile, FiUsers, FiMessageCircle, FiMic } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ChallengeModal = ({ challenge, isOpen, onClose, onComplete, isCompleted, mode, currentPlayer }) => {
  if (!isOpen || !challenge) return null;

  const roles = {
    questioner: currentPlayer,
    storyteller: currentPlayer === 1 ? 2 : 1
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "100%" }}
          className="bg-white rounded-t-[2.5rem] sm:rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 sm:p-8 bg-white border-b border-slate-100 relative flex-shrink-0">
            <button
              onClick={onClose}
              className="absolute top-6 right-6 p-2 text-slate-300 hover:text-slate-900 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl ${challenge.gradient} text-white flex items-center justify-center shadow-md`}>
                <SafeIcon icon={challenge.icon} className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">{challenge.title}</h2>
                {mode === 'duo' && (
                  <div className="flex items-center gap-1 text-[10px] font-black uppercase text-indigo-500 mt-1">
                    <SafeIcon icon={FiUsers} />
                    <span>Duo Challenge</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8 overflow-y-auto max-h-[60vh]">
            {mode === 'duo' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center">
                  <SafeIcon icon={FiMic} className="w-5 h-5 text-indigo-600 mx-auto mb-2" />
                  <p className="text-[9px] font-black uppercase text-indigo-400 tracking-wider mb-1">Questioner</p>
                  <p className="text-sm font-black text-indigo-900">Player {roles.questioner}</p>
                  <p className="text-[10px] text-indigo-600 mt-1 font-medium">Read the prompt</p>
                </div>

                <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl text-center">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-rose-600 mx-auto mb-2" />
                  <p className="text-[9px] font-black uppercase text-rose-400 tracking-wider mb-1">Storyteller</p>
                  <p className="text-sm font-black text-rose-900">Player {roles.storyteller}</p>
                  <p className="text-[10px] text-rose-600 mt-1 font-medium">Answer first</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  {mode === 'duo' ? `Player ${roles.questioner}, ask this:` : 'Ask this question:'}
                </h3>
              </div>
              <p className="text-2xl font-black text-slate-900 leading-[1.15]">
                {challenge.prompt}
              </p>
            </div>

            <div className="p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 space-y-3">
              <div className="flex items-center gap-2 text-slate-900">
                <SafeIcon icon={FiSmile} className="w-4 h-4 text-indigo-500" />
                <h4 className="text-xs font-black uppercase tracking-wider">
                  {mode === 'duo' ? 'Both Players: Share Reflection' : 'Shared Reflection'}
                </h4>
              </div>
              <p className="text-[14px] text-slate-600 leading-relaxed font-medium italic">
                {challenge.reflection}
              </p>
            </div>

            <button
              onClick={() => {
                if (!isCompleted) onComplete(challenge.id);
                onClose();
              }}
              className={`w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all transform active:scale-[0.98] ${
                isCompleted
                  ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                  : 'bg-gradient-to-br from-indigo-500 via-blue-600 to-cyan-400 text-white shadow-xl shadow-indigo-200'
              }`}
            >
              {isCompleted ? (
                <>
                  <SafeIcon icon={FiCheckCircle} />
                  Spark Collected
                </>
              ) : (
                mode === 'duo' ? "We had this chat" : "I had this conversation"
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ChallengeModal;